import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, FileText, Loader2 } from 'lucide-react';
import { confirmWithdrawal, fetchWithdrawals } from '../api/candidate';
import type { CandidateWithdrawal, DeclaredWithdrawal, SaleSubjectKind } from '../types';
import { DocumentRow, WithdrawalLine } from '../components/DocumentRow';
import { WITHDRAWAL_ANCHOR } from '../components/shell/nav';
import { WithdrawalForm, subjectWords } from '../components/WithdrawalForm';
import { Button, Callout, Card, Drawer } from '../components/charte';

// Conditions de vente (T14, 26/10/2026) : rangée comme « Signaler un problème » : menu du compte (ordinateur), « Plus »
// (téléphone), jamais dans les onglets du haut. Sa section « Droit de rétractation » porte « Renoncer au contrat ici »,
// déplacé de « Mes documents » à la demande de l'utilisateur : même panneau, même formulaire, mêmes relectures qu'avant ;
// le délai ouvert ne se dit plus qu'ici. Les conditions générales de vente génériques écrites pour T14 sont retirées
// avant la mise en ligne (décision de l'utilisateur du 26/10 : jamais relues, médiateur absent, présentées comme celles
// de l'école) ; elles reviendront rédigées et relues (`plan-conditions-de-vente.md`).

/** Titre d'une vente : « Commande en ligne FAC-… » (titre de la facture d'un achat), « Contrat … », « Devis … ». */
function saleTitle(kind: SaleSubjectKind, number: string): string {
  return `${kind === 'shop_purchase' ? 'Commande en ligne' : subjectWords(kind).title} ${number}`;
}

/** Section de la page : carte nommée par son titre (région pour les lecteurs d'écran). `anchor` : cible du lien. */
function Section({ id, title, anchor = false, children }: { id: string; title: string; anchor?: boolean; children: ReactNode }) {
  const titleId = `conditions-${id}-titre`;
  return (
    <Card
      id={id}
      aria-labelledby={titleId}
      tabIndex={anchor ? -1 : undefined}
      className={anchor ? 'scroll-mt-20 outline-none' : undefined}
    >
      <h2 id={titleId} className="text-title-sm text-heading">
        {title}
      </h2>
      {children}
    </Card>
  );
}

function Text({ children }: { children: ReactNode }) {
  return <p className="mt-2 text-sm leading-relaxed text-body">{children}</p>;
}

/** Libellé de l'espace élève entre « », d'un seul tenant : jamais un guillemet seul en bout ou en début de ligne. */
function Quoted({ children }: { children: string }) {
  return <span className="whitespace-nowrap">{`« ${children} »`}</span>;
}

export default function ConditionsView() {
  const queryClient = useQueryClient();
  // Ventes conclues à distance de l'élève (T17, T14) : même lecture que « Mes documents ».
  const sales = useQuery({ queryKey: ['candidate', 'withdrawals'], queryFn: fetchWithdrawals });
  const [withdrawing, setWithdrawing] = useState<CandidateWithdrawal | null>(null);

  // Adresse /conditions#retractation : la page s'ouvre sur la section, qui prend le focus (lecteurs d'écran), sous
  // l'en-tête collant (`scroll-mt-20`).
  useEffect(() => {
    if (window.location.hash !== `#${WITHDRAWAL_ANCHOR}`) return;
    const section = document.getElementById(WITHDRAWAL_ANCHOR);
    section?.scrollIntoView({ block: 'start' });
    section?.focus({ preventScroll: true });
  }, []);

  const all = sales.data ?? [];
  // Délai ouvert d'abord, puis rétractations reçues ; jamais un délai terminé.
  const listed = [...all.filter((w) => w.state === 'open'), ...all.filter((w) => w.state === 'withdrawn')];

  async function confirm(id: string, declared: DeclaredWithdrawal) {
    try {
      const sale = await confirmWithdrawal(id, declared);
      if (!sale.withdrawal) throw new Error('Opération indisponible. Réessayez.');
      return sale.withdrawal;
    } finally {
      // Ligne relue (« Rétractation reçue le … ») ; délai passé entre-temps : la ligne aussi. « Documents » relu
      // (pastille « Rétracté », ligne de la facture ou du contrat).
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'withdrawals'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'documents'] });
    }
  }

  return (
    <div className="grid max-w-3xl gap-5 sm:gap-6">
      <div>
        <h1 className="text-title-md text-heading sm:text-title-lg">Conditions de vente</h1>
      </div>

      <Section id={WITHDRAWAL_ANCHOR} title="Droit de rétractation" anchor>
        <Text>
          Un contrat ou un achat conclu à distance (en ligne, sans vous rendre à l’auto-école) peut être annulé pendant le
          délai de rétractation, sans avoir à vous justifier. La date limite de chacun est indiquée ci-dessous. Vous pouvez
          y renoncer ici, avec <Quoted>Renoncer au contrat ici</Quoted>, par le lien reçu par e-mail, ou en écrivant à
          votre auto-école (courrier ou e-mail). Votre auto-école vous rembourse les sommes versées au plus tard 14 jours
          après avoir reçu votre rétractation, par le même moyen de paiement.
        </Text>
        {sales.isPending ? (
          <p className="mt-4 flex items-center text-sm text-secondary">
            <Loader2 className="mr-2 animate-spin" size={16} aria-hidden /> Chargement…
          </p>
        ) : sales.isError && !sales.data ? (
          // Jamais « Aucun contrat ni achat … » sans avoir lu les ventes : ce serait taire un droit ouvert.
          <div className="mt-4 grid justify-items-start gap-3">
            <div role="alert" className="w-full">
              <Callout tone="warn">Vos contrats et achats n’ont pas pu être chargés.</Callout>
            </div>
            <Button size="sm" loading={sales.isFetching} onClick={() => void sales.refetch()}>
              Réessayer
            </Button>
          </div>
        ) : listed.length === 0 ? (
          <p className="mt-4 text-sm text-secondary">Aucun contrat ni achat n’est en cours de délai de rétractation.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100 pt-3.5">
            {listed.map((sale) => {
              const titleId = `retractation-${sale.id}-titre`;
              return (
                <DocumentRow
                  key={sale.id}
                  icon={
                    sale.subject.kind === 'shop_purchase' ? (
                      <FileText size={18} aria-hidden />
                    ) : (
                      <FileSignature size={18} aria-hidden />
                    )
                  }
                  title={saleTitle(sale.subject.kind, sale.subject.number)}
                  titleId={titleId}
                  sub={<WithdrawalLine sale={sale} />}
                >
                  {/* Libellé légal exact ; boutons de même nom : chacun est décrit par le titre de sa ligne. */}
                  {sale.state === 'open' && (
                    <Button size="sm" variant="soft" aria-describedby={titleId} onClick={() => setWithdrawing(sale)}>
                      Renoncer au contrat ici
                    </Button>
                  )}
                </DocumentRow>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Un seul panneau, hors des lignes : il garde l'accusé affiché pendant la relecture de la liste. */}
      <Drawer
        open={!!withdrawing}
        onClose={() => setWithdrawing(null)}
        title="Renoncer au contrat"
        subtitle={withdrawing ? `${subjectWords(withdrawing.subject.kind).title} ${withdrawing.subject.number}` : undefined}
      >
        {withdrawing && (
          <WithdrawalForm
            key={withdrawing.id}
            kind={withdrawing.subject.kind}
            number={withdrawing.subject.number}
            until={withdrawing.until}
            defaults={withdrawing.defaults}
            start="fields"
            onConfirm={(declared) => confirm(withdrawing.id, declared)}
            onCancel={() => setWithdrawing(null)}
          />
        )}
      </Drawer>
    </div>
  );
}
